import { apiClient } from "@/lib/api";
import type { AdminAlertSeverity } from "@/types/admin";
import type {
  NotificationLogParams,
  PaginatedNotificationLog,
} from "@/types/notifications";

export interface Notification {
  id: number;
  title: string;
  body: string;
  is_read: boolean;
  read_at: string | null;
  channel: string;
  created_at: string;
  notification_type: string;
  link: string | null;
  /**
   * Severity bucket carried by broadcast-delivered (system) rows; `null` for
   * ordinary notifications. Reuses the platform `AdminAlertSeverity` scale so
   * the bell can color the row with the shared severity badge (TASK-3939).
   */
  severity: AdminAlertSeverity | null;
  /** True when the row was delivered from an admin broadcast (TASK-3939). */
  is_system: boolean;
  /**
   * Structured, language-neutral params used to localize the row on the client
   * keyed on `notification_type` (BUG-322). Optional: the backend does not emit
   * it on the bell serializer yet, so the i18n mapping falls back to the raw
   * `title` / `body` until a follow-up backend change starts including it.
   */
  extra_data?: Record<string, unknown> | null;
}

export interface PaginatedNotifications {
  results: Notification[];
  next: string | null;
  previous: string | null;
}

export interface UnreadCountResponse {
  count: number;
}

/**
 * Fetches the 20 most recent in-app notifications.
 * GET /api/v1/notifications/?page_size=20
 */
export async function fetchNotifications(): Promise<PaginatedNotifications> {
  const response = await apiClient.get<PaginatedNotifications>(
    "/v1/notifications/",
    { params: { page_size: 20 } },
  );
  return response.data;
}

/**
 * Fetches the count of unread notifications for the current user.
 * GET /api/v1/notifications/unread-count/
 */
export async function fetchUnreadCount(): Promise<UnreadCountResponse> {
  const response = await apiClient.get<UnreadCountResponse>(
    "/v1/notifications/unread-count/",
  );
  return response.data;
}

/**
 * Marks all unread notifications as read.
 * POST /api/v1/notifications/read-all/
 */
export async function markAllRead(): Promise<{ status: string }> {
  const response = await apiClient.post<{ status: string }>(
    "/v1/notifications/read-all/",
  );
  return response.data;
}

/**
 * Marks a single notification as read.
 * POST /api/v1/notifications/{id}/read/
 */
export async function markRead(id: number): Promise<{ status: string }> {
  const response = await apiClient.post<{ status: string }>(
    `/v1/notifications/${id}/read/`,
  );
  return response.data;
}

// ---------------------------------------------------------------------------
// Maintenance banner (tenant-facing read, TASK-3930/3931)
// ---------------------------------------------------------------------------

/**
 * The active maintenance banner as exposed to any authenticated user.
 * `active` is false (and `message` an empty string) when no banner is currently
 * in effect — the window-aware "is it showing right now" computed by the
 * backend.
 */
export interface ActiveMaintenanceBanner {
  active: boolean;
  message: string;
  /**
   * Severity bucket controlling the banner color (TASK-3940). The backend
   * always returns a value (defaulting to `"info"`), reusing the platform
   * `AdminAlertSeverity` scale shared with the broadcast bell.
   */
  severity: AdminAlertSeverity;
  active_from: string | null;
  active_until: string | null;
}

/**
 * Fetch the currently-active platform maintenance banner for the tenant shells
 * (TASK-3931). Any authenticated user may read it. Backed by
 * `GET /api/v1/notifications/maintenance-banner/`; returns
 * `{ active: false, message: "" }` when nothing is active.
 */
export async function getActiveMaintenanceBanner(): Promise<ActiveMaintenanceBanner> {
  const response = await apiClient.get<ActiveMaintenanceBanner>(
    "/v1/notifications/maintenance-banner/",
  );
  return response.data;
}

// ---------------------------------------------------------------------------
// Delivery log (Analytics → Notifications tab, TASK-3831)
// ---------------------------------------------------------------------------

/**
 * Fetches a paginated page of the notification delivery log (TASK-3822).
 * GET /api/v1/notifications/delivery-log/
 *
 * Empty-string/undefined filters are stripped so they are never sent, and
 * `undelivered_only` is only included when true (it filters to pending+failed).
 */
export async function getNotificationDeliveryLog(
  params: NotificationLogParams,
): Promise<PaginatedNotificationLog> {
  const query: Record<string, string | number> = {};

  if (params.channel) query.channel = params.channel;
  if (params.status) query.status = params.status;
  if (params.date_from) query.date_from = params.date_from;
  if (params.date_to) query.date_to = params.date_to;
  if (params.search) query.search = params.search;
  if (params.undelivered_only) query.undelivered_only = "true";
  if (params.page != null) query.page = params.page;

  const response = await apiClient.get<PaginatedNotificationLog>(
    "/v1/notifications/delivery-log/",
    { params: query },
  );
  return response.data;
}

// ---------------------------------------------------------------------------
// Notification templates (Settings → Notification templates tab)
// ---------------------------------------------------------------------------

/** Channel a notification template belongs to. */
export type NotificationTemplateChannel =
  | "client_email"
  | "client_sms"
  | "driver_push";

/** A single notification template row returned by the CRUD endpoint. */
export interface NotificationTemplate {
  id: number;
  notification_type: string;
  channel: NotificationTemplateChannel;
  subject: string;
  body: string;
  is_active: boolean;
  notify_before_min: number | null;
  notify_before_m: number | null;
  created_at?: string;
  updated_at?: string;
}

/** Payload accepted by PATCH on a notification template. */
export interface NotificationTemplateUpdatePayload {
  subject?: string;
  body?: string;
  is_active?: boolean;
}

/** A single available placeholder variable for the editor helper. */
export interface NotificationPlaceholder {
  name: string;
  label: string;
}

/**
 * Placeholder metadata returned by the placeholders endpoint (TASK-3752).
 * `all` lists every known placeholder; `by_channel` groups the relevant subset
 * per channel.
 */
export interface NotificationPlaceholdersResponse {
  all: NotificationPlaceholder[];
  by_channel: Record<string, NotificationPlaceholder[]>;
}

interface NotificationTemplateListResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: NotificationTemplate[];
}

/**
 * Lists the tenant's notification templates.
 * GET /api/v1/settings/notification-templates/
 *
 * The endpoint is paginated ({count,next,previous,results}); we unwrap to the
 * results array. A high page_size is requested so the full template set (up to
 * 21 types × 3 channels = 63 rows) is returned in one page — this is a config
 * surface, not a browsable list, so the editor must show every template.
 */
export async function getNotificationTemplates(): Promise<
  NotificationTemplate[]
> {
  const response = await apiClient.get<NotificationTemplateListResponse>(
    "/v1/settings/notification-templates/",
    { params: { page_size: 200 } },
  );
  return response.data.results;
}

/**
 * Partially updates a single notification template.
 * PATCH /api/v1/settings/notification-templates/{id}/
 */
export async function updateNotificationTemplate(
  id: number,
  payload: NotificationTemplateUpdatePayload,
): Promise<NotificationTemplate> {
  const response = await apiClient.patch<NotificationTemplate>(
    `/v1/settings/notification-templates/${id}/`,
    payload,
  );
  return response.data;
}

/**
 * Fetches the available placeholder variables for the editor's helper panel.
 * GET /api/v1/settings/notification-templates/placeholders/
 */
export async function getNotificationPlaceholders(): Promise<NotificationPlaceholdersResponse> {
  const response = await apiClient.get<NotificationPlaceholdersResponse>(
    "/v1/settings/notification-templates/placeholders/",
  );
  return response.data;
}

/** Draft subject/body sent to the preview endpoint so unsaved edits render. */
export interface NotificationTemplateDraft {
  subject?: string;
  body?: string;
}

/** Rendered preview returned by the preview endpoint (placeholders substituted). */
export interface NotificationTemplatePreview {
  subject: string;
  body: string;
}

/**
 * Renders the given draft (or the stored template, if no draft) with sample
 * data — no message is sent.
 * POST /api/v1/settings/notification-templates/{id}/preview/
 */
export async function previewNotificationTemplate(
  id: number,
  draft: NotificationTemplateDraft,
): Promise<NotificationTemplatePreview> {
  const response = await apiClient.post<NotificationTemplatePreview>(
    `/v1/settings/notification-templates/${id}/preview/`,
    draft,
  );
  return response.data;
}

/** Response from the test-send endpoint when the message is queued. */
export interface NotificationTestSendResponse {
  status: string;
}

/**
 * Sends a single test notification for the template. `recipient` is an email
 * (client_email) or phone (client_sms); it is ignored for driver_push (the
 * push targets the current user's own devices).
 * POST /api/v1/settings/notification-templates/{id}/test-send/
 */
export async function testSendNotificationTemplate(
  id: number,
  recipient: string,
): Promise<NotificationTestSendResponse> {
  const response = await apiClient.post<NotificationTestSendResponse>(
    `/v1/settings/notification-templates/${id}/test-send/`,
    { recipient },
  );
  return response.data;
}

/**
 * Overwrites a single template's subject + body from the seeded defaults.
 * POST /api/v1/settings/notification-templates/{id}/reset-to-default/
 */
export async function resetNotificationTemplate(
  id: number,
): Promise<NotificationTemplate> {
  const response = await apiClient.post<NotificationTemplate>(
    `/v1/settings/notification-templates/${id}/reset-to-default/`,
  );
  return response.data;
}

/** Counts returned when all templates are reset to their defaults. */
export interface NotificationResetAllResponse {
  updated: number;
  created: number;
}

/**
 * Overwrites every matching template from defaults and creates any missing
 * defaults for the tenant.
 * POST /api/v1/settings/notification-templates/reset-all-to-default/
 */
export async function resetAllNotificationTemplates(): Promise<NotificationResetAllResponse> {
  const response = await apiClient.post<NotificationResetAllResponse>(
    "/v1/settings/notification-templates/reset-all-to-default/",
  );
  return response.data;
}
